import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getLoggedInUser, swap, getDateFromMongoDate, accessRightActionCheck } from "utilities";
import { COMMON_ACCESS_ACTION, YES, ENTITY_TYPE, PRODUCT_PLATFORM, COMMENT_RATING, COMMENT_TYPE } from "../../../../../constants";
import CommonDataGrid from "app/components/dataGrid";
import { businessImpactFilter, StringFilter } from "app/components/dataGrid/fliters";
import { useHistory } from "react-router-dom";
import { setInternalGridState } from "../../../../../middleware/actions/gridAction";
import { fetchCompanyComments } from "middleware/services/companyApi";
import ThreeDotMenu from "app/components/threeDotMenu";
import "./index.css";
import BusinessImapactSideBar from "app/components/BusinessImpactSideBar";
import { addUserComment, deleteUserComment, fetchUserComments } from "middleware/services/userApi";
import { useQuery } from "@tanstack/react-query";
import { actionError, actionSuccess } from "middleware/actions/utilityAction";
import { showAlertBox } from "middleware/actions/alertBoxAction";

const businessImpactTemplate = (value) => {
  return <span>{value["Business Impact"] || ""}</span>;
};

const userNameTemplate = (value) => <span style={{ textTransform: "capitalize", paddingLeft: "12px" }}>{value["Client User Name"]}</span>;

const dateTemplate = (value) => {
  const column = value.column.field;
  return ["Comment Date", "Reply Date"].includes(column) && !isNaN(Date.parse(value[`${column}`])) ? (
    <span>{getDateFromMongoDate(new Date(value[`${column}`]))}</span>
  ) : (
    ""
  );
};

const columnNames = {
  id: "id",
  "Client User Name": "userName",
  "Client Company Name": "userCompany",
  "Business Impact": "commentRating",
  "Comment Date": "commentCreatedDate",
  "Reply Date": "replies.commentCreatedDate",
};

const interfaceName = "Business Impact";
const BusinessImpact = (props) => {
  const dispatch = useDispatch();
  let user = getLoggedInUser();
  const { accessRights: aR, companyName } = props;
  const history = useHistory();
  const gridState = useSelector((state) => state.gridState);

  const companyId = props?.computedMatch?.params?.companyId;
  const [commentId, setCommentId] = useState("");
  const [commentData, setCommentData] = useState([]);
  const [showCommentSideBar, setShowCommentSideBar] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const cancelPath = props.path === "/processing/company-old/:companyId/edit" ? "/processing/company-old" : "/company-old";

  let actionAccess = accessRightActionCheck(aR?.moduleName, interfaceName) || [];
  const accessActionViewComments = actionAccess.includes(COMMON_ACCESS_ACTION.VIEW_COMMENTS);
  const accessActionAddReply = actionAccess.includes(COMMON_ACCESS_ACTION.ADD_REPLY);

  const {
    data: commentFetchData,
    isFetching: fetchingComments,
    refetch: refetchComment,
  } = useQuery({
    queryKey: ["comment-data", companyId, showCommentSideBar, commentId],
    queryFn: () => {
      let params = {
        filters: [
          ["commentEntityId", "eq", companyId],
          ["commentIsActive", "eq", YES],
          ["commentProductPlatform", "eq", "Insider V2"],
          ["_id", "eq", commentId],
        ],
        limit: 2000,
        sort: "commentCreatedDate:desc",
      };
      return fetchUserComments(params);
    },
    suspense: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    refetchIntervalInBackground: false,
    enabled: !!commentId && !!showCommentSideBar,
  });

  const actionTemplate = (value) => {
    return (
      <ThreeDotMenu
        rowID={value.id}
        {...(accessActionViewComments
          ? {
              "View Comments": () => {
                viewCommentsClicked(value);
              },
            }
          : {})}
      />
    );
  };

  // formatRowData function required to format rowData for datGrid
  const formatRowData = (rowData) => {
    let formatedRowData = [];
    const columnNamesValues = swap(columnNames);
    formatedRowData = rowData.map((rD) => {
      const newRD = { Id: rD.id };
      Object.keys(rD).forEach((rDK) => {
        if (columnNamesValues[rDK]) {
          newRD[`${columnNamesValues[rDK]}`] = rD[rDK] && rD[rDK] !== "" ? rD[rDK] : "";
        }
        return newRD;
      });
      newRD["Comment Created Date"] = rD.commentCreatedDate ? new Date(rD.commentCreatedDate) : "";
      newRD["Reply Date"] = "";
      if (rD?.replies?.length > 0) {
        const reply = rD.replies?.[0];
        newRD["Reply Date"] = new Date(reply?.commentCreatedDate);
      }
      return newRD;
    });

    return formatedRowData;
  };

  const viewCommentsClicked = (value) => {
    setCommentId(value.id);
    setShowCommentSideBar(true);
  };

  const columnFields = [
    {
      field: "Client User Name",
      type: "String",
      filterTemplate: StringFilter,
      textAlign: "Left",
      showInColumnChooser: false,
      width: 200,
      clipMode: "EllipsisWithTooltip",
      template: userNameTemplate,
    },
    {
      field: "Client Company Name",
      type: "String",
      showInColumnChooser: true,
      textAlign: "Left",
      width: 200,
      filterTemplate: StringFilter,
      clipMode: "EllipsisWithTooltip",
    },
    {
      field: "Business Impact",
      type: "Boolean",
      filterTemplate: businessImpactFilter,
      textAlign: "Left",
      showInColumnChooser: true,
      width: 200,
      template: businessImpactTemplate,
    },
    {
      field: "Comment Date",
      type: "Date",
      template: dateTemplate,
      textAlign: "Center",
      width: 180,
      headerTextAlign: "Center",
      showInColumnChooser: true,
    },
    {
      field: "Reply Date",
      type: "Date",
      template: dateTemplate,
      textAlign: "Center",
      width: 150,
      headerTextAlign: "Center",
      showInColumnChooser: true,
    },
  ];

  if (accessActionViewComments) {
    columnFields.push({
      field: "Action",
      type: "String",
      template: actionTemplate,
      allowSorting: false,
      allowFiltering: false,
      textAlign: "Right",
      headerTextAlign: "Center",
      width: 65,
      showInColumnChooser: false,
      freeze: "Right",
    });
  }

  const getCompanyComments = (params = {}) => {
    if (!companyId) return;
    let fields = ["commentText", "commentCreatedDate", "commentRating", "commentCreatedDate", "replies", "userName", "userCompany"];
    let { filters = [] } = params;
    params.filters = [...filters, ["commentEntityId", "eq", companyId], ["commentIsActive", "eq", YES]];
    if (!params.sort) params.sort = "_id:desc";
    if (!gridState.internalGridState.recordVisited) {
      if (!params.skip) {
        params.skip = 0;
      }
      dispatch(setInternalGridState(params.skip, params.sort, params.filters));
    }
    if (gridState.internalGridState.recordVisited) {
      params = {
        filters: gridState.internalGridState.filtersParams,
        sort: gridState.internalGridState.sortRecords,
        skip: gridState.internalGridState.skipRecords,
      };
      dispatch(setInternalGridState(params.skip, params.sort, params.filters));
    }
    if (fields?.length) params.fields = fields;
    return fetchCompanyComments(params);
  };

  const reset = useCallback(() => {
    setCommentData({});
    setShowCommentSideBar(false);
  });

  // handle save comment
  const onSave = async (payload) => {
    try {
      const payloadData = {
        commentEntityId: companyId,
        commentText: payload.reply.trim(),
        commentType: COMMENT_TYPE.REPLY,
        commentParentId: commentId,
        commentRating: COMMENT_RATING.OPPORTUNITY,
        commentEntityType: ENTITY_TYPE.COMPANY,
        commentProductPlatform: PRODUCT_PLATFORM.INSIDER_V2,
      };
      await addUserComment(user._id, payloadData);
      setDeleteId(commentId);
      reset();
      dispatch(actionSuccess("Your reply was successfully saved."));
      setTimeout(() => {
        setCommentId(null);
        setDeleteId(null);
      }, [200]);
    } catch (err) {
      console.log("onSave", err);
      dispatch(actionError("Something went wrong!"));
    }
  };

  // handle delete reply
  const deleteReply = async function (id) {
    try {
      await deleteUserComment(id);
      await refetchComment();
      dispatch(actionSuccess("Your reply was successfully deleted."));
    } catch (err) {
      console.log("onDeleteReplyClick", err);
      dispatch(actionError("Something went wrong!"));
    }
  };
  // show alert dialog box
  const handleDeleteReplyClick = (id) => {
    dispatch(
      showAlertBox({
        okCallback: () => deleteReply(id),
        okText: "Delete",
        cancelText: "Cancel",
        content: "Are you sure you want to delete ?",
        title: "dialogAlertCssWarning",
      })
    );
  };

  useEffect(() => {
    if (!commentFetchData?.data?.data?.length) return setCommentData({});
    setCommentData(commentFetchData?.data?.data);
  }, [commentFetchData]);

  return (
    <div className={"business-imapct-container content-wrapper"}>
      <div className={"gennx-business-imapact-container"}>
        <CommonDataGrid
          gridTitle="BusinessImpact"
          fetch={getCompanyComments}
          columnNames={columnNames}
          columnFields={columnFields}
          formatRowData={formatRowData}
          isChildGrid={true}
          deleteMany={false}
          deleteId={deleteId}
        />
      </div>
      <div className="business-impact-footer">
        {(props.selectedIndex > 0 || props.selectedIndex === undefined) && (
          <button
            type="button"
            className="btn btn-outline-dark cy-btn "
            onClick={() => {
              props.setCurrentTab(props.selectedIndex - 1);
            }}
          >
            Previous
          </button>
        )}
        <button type="button" className="btn btn-outline-dark cy-btn" onClick={() => history.push(`${cancelPath}`)}>
          Cancel
        </button>
      </div>
      {showCommentSideBar && (
        <BusinessImapactSideBar
          data={commentData}
          onClose={() => {
            reset();
            setDeleteId("0");
            setTimeout(() => {
              setDeleteId(null);
            }, [200]);
            setCommentId(null);
          }}
          onSave={onSave}
          companyName={companyName}
          handleDeleteReplyClick={handleDeleteReplyClick}
          loader={fetchingComments}
          accessActionAddReply={accessActionAddReply}
          loggedInUserId={user?._id}
        />
      )}
    </div>
  );
};

export default BusinessImpact;
